import { useState } from "react";
import { ChevronDown, ChevronUp, Info, Layers, StickyNote } from "lucide-react";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";
import { LANE_META } from "./task-type-meta";

function LayerChip({
  on,
  color,
  label,
  shortcut,
  onClick,
}: {
  on: boolean;
  color: string;
  label: string;
  shortcut: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} (${shortcut})`}
      className={
        "inline-flex h-7 items-center gap-1.5 rounded-full border pl-1.5 pr-2 text-[11.5px] font-semibold transition-all " +
        (on
          ? "border-zinc-200 bg-white text-zinc-700"
          : "border-transparent bg-zinc-100/70 text-zinc-400 line-through")
      }
    >
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: 3,
          background: on ? color : "#cbd5e1",
        }}
      />
      {label}
    </button>
  );
}

/** Map layer toggles + marker legend (chrome §7), docked top-right. Wired to
 * the shared shortcuts store so the marker layers and the P/D/C/U/W/O/T
 * keyboard shortcuts stay in sync. */
export function MapControls() {
  const s = useShortcutsStore();
  const [layersOpen, setLayersOpen] = useState(true);
  const [legendOpen, setLegendOpen] = useState(false);

  return (
    <div className="pointer-events-auto absolute right-3 top-3 z-20 flex flex-col items-end gap-2">
      <div className="overflow-hidden rounded-xl bg-white/95 shadow-card">
        <button
          type="button"
          onClick={() => setLayersOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-3 py-2 text-[12px] font-bold text-zinc-700"
        >
          <Layers className="h-3.5 w-3.5" /> Layers
          {layersOpen ? (
            <ChevronUp className="ml-auto h-3.5 w-3.5 text-zinc-400" />
          ) : (
            <ChevronDown className="ml-auto h-3.5 w-3.5 text-zinc-400" />
          )}
        </button>
        {layersOpen && (
          <div className="flex max-w-[260px] flex-wrap gap-1.5 px-2.5 pb-2.5">
            <LayerChip
              on={s.showPickups}
              color={LANE_META.pickup.color}
              label="Pickups"
              shortcut="P"
              onClick={() => s.setShowPickups(!s.showPickups)}
            />
            <LayerChip
              on={s.showDeliveries}
              color={LANE_META.delivery.color}
              label="Deliveries"
              shortcut="D"
              onClick={() => s.setShowDeliveries(!s.showDeliveries)}
            />
            <LayerChip
              on={s.showCarrierDropoffs}
              color={LANE_META.dropoff.color}
              label="Drop-offs"
              shortcut="C"
              onClick={() => s.setShowCarrierDropoffs(!s.showCarrierDropoffs)}
            />
            <LayerChip
              on={s.showReturns}
              color={LANE_META.return.color}
              label="Returns"
              shortcut="U"
              onClick={() => s.setShowReturns(!s.showReturns)}
            />
            <LayerChip
              on={s.showDepots}
              color="#27272a"
              label="Dispatch centers"
              shortcut="W"
              onClick={() => s.setShowDepots(!s.showDepots)}
            />
            <LayerChip
              on={s.showOperators}
              color="#16a34a"
              label="Drivers"
              shortcut="O"
              onClick={() => s.setShowOperators(!s.showOperators)}
            />
            <LayerChip
              on={s.tripFilter.showAll}
              color="#4f46e5"
              label="Trips"
              shortcut="T"
              onClick={() => s.toggleAllTrips()}
            />
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl bg-white/95 shadow-card">
        <button
          type="button"
          onClick={() => setLegendOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-3 py-2 text-[12px] font-bold text-zinc-700"
        >
          <Info className="h-3.5 w-3.5" /> Legend
          {legendOpen ? (
            <ChevronUp className="ml-auto h-3.5 w-3.5 text-zinc-400" />
          ) : (
            <ChevronDown className="ml-auto h-3.5 w-3.5 text-zinc-400" />
          )}
        </button>
        {legendOpen && (
          <div className="max-w-[260px] px-3 pb-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {(["pickup", "delivery", "dropoff", "return"] as const).map(
                (k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span
                      style={{
                        width: 11,
                        height: 11,
                        borderRadius: 3,
                        background: LANE_META[k].color,
                      }}
                    />
                    <span className="text-[12px] text-zinc-700">
                      {LANE_META[k].label}
                    </span>
                  </div>
                )
              )}
            </div>
            <div className="my-2 h-px bg-zinc-200" />
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span
                  className="grid place-items-center bg-white text-zinc-900"
                  style={{
                    width: 17,
                    height: 17,
                    borderRadius: 5,
                    border: "1.5px solid #0b0e14",
                  }}
                >
                  <StickyNote className="h-2.5 w-2.5" />
                </span>
                <span className="text-[12px] text-zinc-700">Notes</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className="grid place-items-center text-white"
                  style={{
                    width: 17,
                    height: 17,
                    borderRadius: 5,
                    border: "1.5px solid #0b0e14",
                    background: "#0d9488",
                  }}
                >
                  <span className="text-[10px] font-bold">+</span>
                </span>
                <span className="text-[12px] text-zinc-700">Add-ons</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
